import { useState } from 'react';

const Support = () => {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "How long does it take to get my test results?",
      answer: "Typically, you'll receive your test results within 3-5 business days after we receive your sample at our lab. You'll get an email notification as soon as your results are ready."
    },
    {
      question: "What should I do if I haven't received my kit?",
      answer: "If you haven't received your testing kit within 7-10 business days, please contact our support team. We'll help track your shipment and arrange a replacement if needed."
    },
    {
      question: "How do I register my testing kit?",
      answer: "You can register your kit by going to the Dashboard and clicking 'Register New Kit'. You'll need the unique kit ID found on your testing kit packaging."
    },
    {
      question: "Can I track my sample once it's sent to the lab?",
      answer: "Yes! Once your sample is received at our lab, you can track its progress through your account dashboard. You'll also receive email updates at each stage."
    },
    {
      question: "What if I have questions about my test results?",
      answer: "Our medical team is available to help explain your results. Contact support and we'll connect you with a healthcare professional who can answer your questions."
    }
  ];

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-brand-gray-50 to-brand-gray-100">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-brand-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-brand-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-brand-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-brand-gray-900 mb-4">
              We're Here to Help
            </h1>
            <p className="text-xl text-brand-gray-600 max-w-3xl mx-auto">
              Have questions about your testing process or results? Our support team is ready to assist you with any concerns or inquiries.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Contact Methods */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-brand-gray-900 mb-8">Get in Touch</h2>
            
            {/* Email Support */}
            <div className="bg-white rounded-2xl shadow-lg border border-brand-gray-200 p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-brand-blue-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-6 h-6 text-brand-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-brand-gray-900 mb-2">Email Support</h3>
                  <p className="text-brand-gray-600 mb-4">
                    Send us an email and we'll respond within 24 hours during business days.
                  </p>
                  <a 
                    href="mailto:support@jackathome.com"
                    className="inline-flex items-center text-brand-blue-500 hover:text-brand-blue-600 font-semibold transition-colors duration-300"
                  >
                    support@jackathome.com
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="text-3xl font-bold text-brand-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={faq.question.toString()} className="bg-white rounded-2xl shadow-lg border border-brand-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-brand-gray-50 transition-colors duration-300"
                  >
                    <h3 className="text-lg font-semibold text-brand-gray-900 pr-4">{faq.question}</h3>
                    <svg 
                      className={`w-5 h-5 text-brand-gray-500 transition-transform duration-300 flex-shrink-0 ${
                        activeFaq === index ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {activeFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-brand-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Help Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-brand-gray-200 p-8 text-center">
          <h3 className="text-2xl font-bold text-brand-gray-900 mb-4">Still Need Help?</h3>
          <p className="text-brand-gray-600 text-lg mb-6 max-w-2xl mx-auto">
            If you can't find what you're looking for, don't hesitate to reach out. Our team is committed to providing you with the best possible support experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:support@jackathome.com"
              className="inline-flex items-center justify-center px-8 py-3 bg-brand-blue-500 hover:bg-brand-blue-600 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send Email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;